// @tennis-matches/services/trainer/drills

import { getSupabaseClient } from "../supabase/index.js";
import { registerHubCacheClear } from "../hub/hub-scoped-cache.js";
import { normalizeDrillPedagogy } from "./drillPedagogy.js";
import { normalizeIdealPlayerCounts } from "./drillPlayerCounts.js";
import {
  normalizeDrillVariation,
  variationModelsToSyncRows,
} from "./drillVariations.js";
import {
  DRILL_TAG_GROUP_KEYS,
  LEGACY_DRILL_TAG_KEY_MAP,
  getDrillTagLabel,
  normalizeDrillTagKey,
} from "./drillTagKeys.js";
import { syncDrillMetadataLinksDualWrite } from "./trainingMetadata.js";

function toDrill(row) {
  if (!row) return null;
  return {
    id: row.id,
    category: row.category,
    level: row.level,
    difficulty: row.difficulty,
    titleKey: row.title_key,
    groupSizeKey: row.group_size_key,
    durationKey: row.duration_key,
    durationMinutesMin: row.duration_minutes_min,
    durationMinutesMax: row.duration_minutes_max,
    isGlobal: row.is_global === true,
    titleOverride: row.title_override,
    descriptionOverride: row.description_override,
    groupSizeOverride: row.group_size_override,
    idealPlayerCounts: row.ideal_player_counts || [2],
    idealOverFour: row.ideal_over_four === true,
    variationOverrides: row.variation_overrides || [],
    coachingTipOverrides: row.coaching_tip_overrides || [],
    tagKeys: row.tag_keys || [],
    descriptionKey: row.description_key,
    variationKeys: row.variation_keys || [],
    coachingTipKeys: row.coaching_tip_keys || [],
    pedagogy: normalizeDrillPedagogy(row.pedagogy),
  };
}

function toRow(drill) {
  if (!drill) return null;
  const r = {};
  if (drill.id != null) r.id = String(drill.id);
  if (drill.category != null) r.category = drill.category;
  if (drill.level != null) r.level = drill.level;
  if (drill.difficulty != null) r.difficulty = drill.difficulty;
  if (drill.titleKey != null) r.title_key = drill.titleKey;
  if (drill.groupSizeKey != null) r.group_size_key = drill.groupSizeKey;
  if (drill.durationKey != null) r.duration_key = drill.durationKey;
  if (drill.durationMinutesMin != null)
    r.duration_minutes_min = drill.durationMinutesMin;
  if (drill.durationMinutesMax != null)
    r.duration_minutes_max = drill.durationMinutesMax;
  if (drill.isGlobal != null) r.is_global = !!drill.isGlobal;
  if (drill.titleOverride !== undefined) r.title_override = drill.titleOverride;
  if (drill.descriptionOverride !== undefined)
    r.description_override = drill.descriptionOverride;
  if (drill.groupSizeOverride !== undefined)
    r.group_size_override = drill.groupSizeOverride;
  if (drill.idealPlayerCounts != null) {
    r.ideal_player_counts = normalizeIdealPlayerCounts(drill.idealPlayerCounts);
  }
  if (drill.idealOverFour != null) r.ideal_over_four = !!drill.idealOverFour;
  if (drill.variationOverrides != null) {
    r.variation_overrides = Array.isArray(drill.variationOverrides)
      ? drill.variationOverrides
      : [];
  }
  if (drill.coachingTipOverrides != null) {
    r.coaching_tip_overrides = Array.isArray(drill.coachingTipOverrides)
      ? drill.coachingTipOverrides
      : [];
  }
  if (drill.tagKeys != null)
    r.tag_keys = Array.isArray(drill.tagKeys) ? drill.tagKeys : [];
  if (drill.descriptionKey != null) r.description_key = drill.descriptionKey;
  if (drill.variationKeys != null)
    r.variation_keys = Array.isArray(drill.variationKeys)
      ? drill.variationKeys
      : [];
  if (drill.coachingTipKeys != null)
    r.coaching_tip_keys = Array.isArray(drill.coachingTipKeys)
      ? drill.coachingTipKeys
      : [];
  if (drill.pedagogy != null)
    r.pedagogy = normalizeDrillPedagogy(drill.pedagogy);
  return r;
}

async function __resolveTagMetadataIds(supabase, tagKeys) {
  const keys = (tagKeys || [])
    .map((k) => normalizeDrillTagKey(k))
    .filter(Boolean);
  if (keys.length === 0) return [];

  const { data: items } = await supabase
    .from("exercise_metadata_items")
    .select("id, item_key, label")
    .in("group_key", DRILL_TAG_GROUP_KEYS)
    .eq("is_active", true);

  const byKey = new Map((items || []).map((i) => [i.item_key, i.id]));
  const byLabel = new Map(
    (items || []).map((i) => [
      String(i.label || "")
        .trim()
        .toLowerCase(),
      i.id,
    ]),
  );
  const byLegacy = new Map(
    Object.entries(LEGACY_DRILL_TAG_KEY_MAP)
      .map(([legacy, itemKey]) => {
        const id = byKey.get(itemKey);
        return id ? [legacy, id] : null;
      })
      .filter(Boolean),
  );

  const ids = [];
  for (const key of keys) {
    const norm = key.toLowerCase();
    const id = byKey.get(key) || byLegacy.get(key) || byLabel.get(norm);
    if (id) ids.push(id);
  }
  return [...new Set(ids)];
}

async function syncDrillRelations(drillId, drill) {
  const supabase = await getSupabaseClient();
  if (!supabase || !drillId) return { ok: false, error: "no-supabase" };

  const tagIds = await __resolveTagMetadataIds(supabase, drill.tagKeys);
  const variationRows = Array.isArray(drill.variationModels)
    ? variationModelsToSyncRows(drill.variationModels)
    : Array.isArray(drill.variationOverrides)
      ? variationModelsToSyncRows(
          drill.variationOverrides.map(normalizeDrillVariation),
        )
      : Array.isArray(drill.variationItems)
        ? variationModelsToSyncRows(
            drill.variationItems.map(normalizeDrillVariation),
          )
        : [];
  const tips = Array.isArray(drill.coachingTipOverrides)
    ? drill.coachingTipOverrides
    : Array.isArray(drill.coachingTipItems)
      ? drill.coachingTipItems
      : [];

  await supabase
    .from("exercise_drill_tag_links")
    .delete()
    .eq("drill_id", drillId);
  const { error: delVarErr } = await supabase
    .from("exercise_drill_variations")
    .delete()
    .eq("drill_id", drillId);
  if (delVarErr) return { ok: false, error: delVarErr };
  const { error: delTipsErr } = await supabase
    .from("exercise_drill_coaching_tips")
    .delete()
    .eq("drill_id", drillId);
  if (delTipsErr) return { ok: false, error: delTipsErr };

  if (tagIds.length > 0) {
    const { error: tagInsErr } = await supabase
      .from("exercise_drill_tag_links")
      .insert(
        tagIds.map((metadataItemId) => ({
          drill_id: drillId,
          metadata_item_id: metadataItemId,
        })),
      );
    if (tagInsErr) return { ok: false, error: tagInsErr };
  }

  const metaRes = await syncDrillMetadataLinksDualWrite(
    drillId,
    drill,
    tagIds,
    supabase,
  );
  if (!metaRes.ok) return metaRes;

  if (variationRows.length > 0) {
    const { error: varInsErr } = await supabase
      .from("exercise_drill_variations")
      .insert(
        variationRows.map((row) => ({
          drill_id: drillId,
          sort_order: row.sort_order,
          content: row.content,
          adaptations: row.adaptations || [],
        })),
      );
    if (varInsErr) return { ok: false, error: varInsErr };
  }

  if (tips.length > 0) {
    const { error: tipsInsErr } = await supabase
      .from("exercise_drill_coaching_tips")
      .insert(
        tips
          .map((content, index) => ({
            drill_id: drillId,
            sort_order: index + 1,
            content: String(content || "").trim(),
          }))
          .filter((row) => row.content),
      );
    if (tipsInsErr) return { ok: false, error: tipsInsErr };
  }

  return { ok: true };
}

let __legacyDrillEnrichUnavailable = false;

function __isLegacyTableUnavailable(error) {
  if (!error) return false;
  const code = String(error.code || "");
  const status = String(error.status || error.statusCode || "");
  const msg = String(error.message || error).toLowerCase();
  return (
    code === "PGRST205" ||
    code === "42P01" ||
    code === "42501" ||
    code === "PGRST301" ||
    status === "403" ||
    msg.includes("does not exist") ||
    msg.includes("permission denied") ||
    msg.includes("row-level security")
  );
}

function __enrichDrillsFromRowData(drills) {
  return drills.map((d) => {
    const fallbackTags = (d.tagKeys || [])
      .map(normalizeDrillTagKey)
      .filter(Boolean)
      .map((itemKey) => ({ itemKey, label: getDrillTagLabel(itemKey) }));
    const variations = Array.isArray(d.variationOverrides)
      ? d.variationOverrides
      : [];
    return {
      ...d,
      tags: fallbackTags,
      variationItems: variations
        .map((item) =>
          typeof item === "string" ? item : String(item?.content || ""),
        )
        .filter(Boolean),
      variationItemsDetailed: variations.map((item) =>
        normalizeDrillVariation(
          typeof item === "string" ? { content: item } : item,
        ),
      ),
      coachingTipItems: Array.isArray(d.coachingTipOverrides)
        ? d.coachingTipOverrides
        : [],
    };
  });
}

async function __enrichDrills(drills, supabaseClient) {
  const supabase = supabaseClient || (await getSupabaseClient());
  if (!supabase) return drills;
  const ids = drills.map((d) => d.id).filter(Boolean);
  if (ids.length === 0) return drills;

  if (__legacyDrillEnrichUnavailable) {
    return drills.map((d) => ({
      ...d,
      tags: (d.tagKeys || [])
        .map(normalizeDrillTagKey)
        .filter(Boolean)
        .map((itemKey) => ({
          itemKey,
          label: getDrillTagLabel(itemKey),
        })),
      variationItems: [],
      variationItemsDetailed: [],
      coachingTipItems: [],
    }));
  }

  const [tagsRes, varRes, tipsRes] = await Promise.all([
    supabase
      .from("exercise_drill_tag_links")
      .select(
        "drill_id, metadata_item_id, item:exercise_metadata_items(id, group_key, item_key, label)",
      )
      .in("drill_id", ids),
    supabase
      .from("exercise_drill_variations")
      .select("drill_id, sort_order, content, adaptations")
      .in("drill_id", ids)
      .order("sort_order"),
    supabase
      .from("exercise_drill_coaching_tips")
      .select("drill_id, sort_order, content")
      .in("drill_id", ids)
      .order("sort_order"),
  ]);

  if (
    __isLegacyTableUnavailable(tagsRes.error) ||
    __isLegacyTableUnavailable(varRes.error) ||
    __isLegacyTableUnavailable(tipsRes.error)
  ) {
    if (typeof window === "undefined") __legacyDrillEnrichUnavailable = true;
    return __enrichDrillsFromRowData(drills);
  }

  const tagsByDrill = new Map();
  for (const row of tagsRes.data || []) {
    const list = tagsByDrill.get(row.drill_id) || [];
    if (row.item) {
      list.push({
        id: row.item.id,
        groupKey: row.item.group_key,
        itemKey: row.item.item_key,
        label: row.item.label,
      });
    }
    tagsByDrill.set(row.drill_id, list);
  }

  const varsByDrill = new Map();
  const varsDetailedByDrill = new Map();
  for (const row of varRes.data || []) {
    const list = varsByDrill.get(row.drill_id) || [];
    list.push(row.content);
    varsByDrill.set(row.drill_id, list);
    const detailed = varsDetailedByDrill.get(row.drill_id) || [];
    detailed.push(
      normalizeDrillVariation({
        content: row.content,
        adaptations: row.adaptations,
      }),
    );
    varsDetailedByDrill.set(row.drill_id, detailed);
  }

  const tipsByDrill = new Map();
  for (const row of tipsRes.data || []) {
    const list = tipsByDrill.get(row.drill_id) || [];
    list.push(row.content);
    tipsByDrill.set(row.drill_id, list);
  }

  return drills.map((d) => {
    const linkedTags = tagsByDrill.get(d.id) || [];
    const fallbackTags = (d.tagKeys || [])
      .map(normalizeDrillTagKey)
      .filter(Boolean)
      .map((itemKey) => ({ itemKey, label: getDrillTagLabel(itemKey) }));
    return {
      ...d,
      tags: linkedTags.length > 0 ? linkedTags : fallbackTags,
      variationItems: varsByDrill.get(d.id) || [],
      variationItemsDetailed: varsDetailedByDrill.get(d.id) || [],
      coachingTipItems: tipsByDrill.get(d.id) || [],
    };
  });
}

export async function seedDefaultDrills() {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const { data, error } = await supabase.rpc("trainer_seed_default_drills");
    if (error) return { ok: false, error };
    return { ok: true, seeded: data?.seeded === true };
  } catch (e) {
    return { ok: false, error: e };
  }
}

const GLOBAL_DRILLS_CACHE_MS = 30000;
/** @type {Promise<{ok: boolean, data?: Array, error?: any}>|null} */
let __globalDrillsInflight = null;
/** @type {{ ts: number, value: {ok: boolean, data?: Array, error?: any} }|null} */
let __globalDrillsCache = null;

export function clearGlobalDrillsCache() {
  __globalDrillsCache = null;
  __globalDrillsInflight = null;
}

registerHubCacheClear(clearGlobalDrillsCache);

export async function loadGlobalDrillsWithSupabase(supabase, options = {}) {
  const force = options?.force === true;
  const now = Date.now();
  if (
    !force &&
    __globalDrillsCache &&
    now - __globalDrillsCache.ts < GLOBAL_DRILLS_CACHE_MS
  ) {
    return __globalDrillsCache.value;
  }
  if (!force && __globalDrillsInflight) return __globalDrillsInflight;

  const run = (async () => {
    try {
      if (!supabase) return { ok: false, error: "no-supabase" };
      const { data, error } = await supabase
        .from("trainer_drills")
        .select("*")
        .eq("is_global", true)
        .order("created_at", { ascending: false });
      if (error) return { ok: false, error };
      const drills = (data || []).map((r) => toDrill(r));
      const enriched = await __enrichDrills(drills, supabase);
      return { ok: true, data: enriched };
    } catch (e) {
      return { ok: false, error: e };
    }
  })();

  const promise = run
    .then((value) => {
      if (value?.ok) {
        __globalDrillsCache = { ts: Date.now(), value };
      }
      return value;
    })
    .finally(() => {
      if (__globalDrillsInflight === promise) __globalDrillsInflight = null;
    });
  __globalDrillsInflight = promise;
  return promise;
}

export async function loadDrills() {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };

    const { data, error } = await supabase
      .from("trainer_drills")
      .select("*")
      .order("is_global", { ascending: false })
      .order("created_at", { ascending: false });
    if (error) return { ok: false, error };

    const drills = (data || []).map((r) => toDrill(r));
    const enriched = await __enrichDrills(drills);
    return { ok: true, data: enriched };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function getDrillById(id) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const key = String(id || "").trim();
    if (!key) return { ok: false, error: "id-required" };
    const { data, error } = await supabase
      .from("trainer_drills")
      .select("*")
      .eq("id", key)
      .maybeSingle();
    if (error) return { ok: false, error };
    if (!data) return { ok: true, data: null };
    const base = toDrill(data);
    const enriched = await __enrichDrills([base]);
    return { ok: true, data: enriched[0] || base };
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function createDrill(drill) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const row = toRow(drill);
    if (!row.id) {
      row.id = crypto.randomUUID ? crypto.randomUUID() : `d_${Date.now()}`;
    }
    const { data, error } = await supabase
      .from("trainer_drills")
      .insert([row])
      .select()
      .single();
    if (error) return { ok: false, error };
    await syncDrillRelations(data.id, drill);
    return getDrillById(data.id);
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function updateDrill(id, updates) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const key = String(id || "").trim();
    if (!key) return { ok: false, error: "id-required" };
    const row = toRow(updates);
    delete row.id;
    const { data, error } = await supabase
      .from("trainer_drills")
      .update(row)
      .eq("id", key)
      .select()
      .single();
    if (error) return { ok: false, error };
    const drill = toDrill(data);
    const hasRelations =
      updates.tagKeys != null ||
      updates.variationOverrides != null ||
      updates.variationModels != null ||
      updates.coachingTipOverrides != null ||
      updates.variationItems != null ||
      updates.coachingTipItems != null;
    if (hasRelations) {
      const syncRes = await syncDrillRelations(key, { ...drill, ...updates });
      if (!syncRes.ok) return syncRes;
    }
    return getDrillById(key);
  } catch (e) {
    return { ok: false, error: e };
  }
}

export async function deleteDrill(id) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, error: "no-supabase" };
    const key = String(id || "").trim();
    if (!key) return { ok: false, error: "id-required" };
    const { error } = await supabase
      .from("trainer_drills")
      .delete()
      .eq("id", key);
    if (error) return { ok: false, error };
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e };
  }
}
